import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Avatar,
  Chip,
  List,
  ListItem,
  Button,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  AttachFile as AttachFileIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  AccessTime as AccessTimeIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import moment from 'moment';
import { pathOr } from 'ramda';
import File from '../../../features/bpDetails/Components/File';
import { getFileTypeName } from '../../../utils/bpData';
import {
  retryEmailAttachmentsRoutine,
  retryEmailAttachmentRoutine
} from '../../../features/inbox/ducks/actions';
import {
  getRetryingAttachmentsStatus,
  getRetryingAttachmentStatus
} from '../../../features/inbox/ducks/selectors';
import { getFileTypes } from '../../../ducks/files/selectors';
import { hideEmail } from 'utils/user';

const EmailDetail = ({ email, buildingPassportId, onRetrySuccess, onBack }) => { // onBack added
  const dispatch = useDispatch();
  const retryingAttachmentsStatus = useSelector(getRetryingAttachmentsStatus);
  const retryingAttachmentStatus = useSelector(getRetryingAttachmentStatus);
  const fileTypes = useSelector(getFileTypes);
  const [showReadByDialog, setShowReadByDialog] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  if (!email) {
    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f7f8fd',
          p: 4
        }}
      >
        <EmailIcon sx={{ fontSize: 64, color: '#e0e0e0', mb: 2 }} />
        <Typography variant="h5" color="text.secondary" sx={{ mb: 1, fontWeight: 500 }}>
          No Email Selected
        </Typography>
        <Typography variant="body1" color="text.secondary" textAlign="center">
          Choose an email from the list to view its contents, attachments, and details.
        </Typography>
      </Box>
    );
  }

  const getSenderDisplay = () => {
    if (email.isFromRegisteredUser && email.senderUser?.name) {
      return email.senderUser.name;
    }
    return email.fromName || (email.fromEmail ? hideEmail(email.fromEmail) : 'Unknown');
  };

  const getSenderInitials = () => {
    const name = getSenderDisplay();
    if (!name) return '??';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getAttachmentStatusIcon = (attachment) => {
    if (attachment.is_processed && attachment.file_id) {
      return <CheckCircleIcon color="success" fontSize="small" />;
    }
    if (attachment.processing_errors) {
      return <ErrorIcon color="error" fontSize="small" />;
    }
    if (!attachment.is_processed) {
      return <ScheduleIcon color="warning" fontSize="small" />;
    }
    return <AttachFileIcon color="action" fontSize="small" />;
  };

  const getAttachmentStatusText = (attachment) => {
    if (attachment.is_processed && attachment.file_id) {
      return 'Processed';
    }
    if (attachment.processing_errors) {
      return 'Failed';
    }
    if (!attachment.is_processed) {
      return 'Processing...';
    }
    return 'Unknown';
  };

  const handleRetryAttachment = (attachment) => {
    dispatch(retryEmailAttachmentRoutine.trigger({
      bpId: buildingPassportId,
      emailId: email.id,
      attachmentId: attachment.id
    }));
  };

  const handleRetryAllAttachments = () => {
    dispatch(retryEmailAttachmentsRoutine.trigger({
      bpId: buildingPassportId,
      emailId: email.id
    }));
  };

  const hasFailedAttachments = email.failedAttachmentsInfo?.length > 0;
  const hasProcessingAttachments = false;

  const isRetryingAll = retryingAttachmentsStatus[email.id] || false;

  const handleFileUpdate = () => {
    console.log('File updated');
  };

  const cleanHtmlBody = (html) => {
    if (!html) return '';
    if (isMobile) {
      return html.replace(/<img /g, '<img style="max-width: 100%; height: auto;" ')
        .replace(/<table /g, '<table style="max-width: 100%; display: block; overflow-x: auto;" ');
    }
    return html;
  };

  return (
    <Box sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      bgcolor: 'white',
      overflowY: isMobile ? 'auto' : 'hidden'
    }}>

      <Box sx={{
        p: { xs: 0.5, md: 2 },
        borderBottom: '1px solid #e6e6e6',
        bgcolor: 'white'
      }}>
        {isMobile && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 0.5 }}>
            <IconButton onClick={onBack} edge="start" sx={{ color: '#5f6368', p: 0.5 }}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="subtitle2" sx={{ color: '#5f6368', fontWeight: 600 }}>Back</Typography>
          </Box>
        )}

        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <Avatar
            sx={{
              width: 40,
              height: 40,
              backgroundColor: email.isFromRegisteredUser ? '#D24724' : '#b2b2b2',
              fontFamily: 'FuturaPT-Book, Open Sans, sans-serif',
              fontSize: '1rem'
            }}
          >
            {getSenderInitials()}
          </Avatar>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="h6" sx={{
              fontWeight: 600,
              mb: 0.5,
              fontFamily: 'FuturaPT-Book, Open Sans, sans-serif',
              color: '#0C1229',
              wordBreak: 'break-word',
              fontSize: isMobile ? '1.1rem' : '1.25rem'
            }}>
              {email.subject || '(No Subject)'}
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3, mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonIcon fontSize="small" sx={{ color: '#84889C' }} />
                <Typography variant="body2" sx={{
                  fontFamily: 'FuturaPT-Book, Open Sans, sans-serif',
                  color: '#0C1229',
                  fontWeight: 500
                }}>
                  {getSenderDisplay()}
                </Typography>
                {email.isFromRegisteredUser && email.senderBpRole && (
                  <Chip
                    label={
                      email.senderBpRole.isRevoked
                        ? `Previous Building Passport ${email.senderBpRole.role || 'User'}`
                        : `Building Passport ${email.senderBpRole.role || 'User'}`
                    }
                    size="small"
                    sx={{
                      bgcolor: email.senderBpRole.isRevoked ? '#e0e0e0' : '#fcf4f2',
                      color: email.senderBpRole.isRevoked ? '#666666' : '#D24724',
                      fontSize: '0.7rem',
                      height: '18px'
                    }}
                  />
                )}
                {!email.isFromRegisteredUser && email.fromEmail && (
                  <Typography variant="caption" sx={{
                    color: '#84889C',
                    fontFamily: 'FuturaPT-Book, Open Sans, sans-serif',
                    ml: 1
                  }}>
                    {hideEmail(email.fromEmail)}
                  </Typography>
                )}
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AccessTimeIcon fontSize="small" sx={{ color: '#84889C' }} />
                <Typography variant="body2" sx={{
                  fontFamily: 'FuturaPT-Book, Open Sans, sans-serif',
                  color: '#84889C'
                }}>
                  {moment(email.receivedAt).format('MMMM D, YYYY [at] h:mm A')}
                </Typography>
              </Box>
            </Box>

            {email.readCount > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  cursor: 'pointer',
                  p: 0,
                  borderRadius: 1,
                  '&:hover': {
                    bgcolor: '#f5f5f5'
                  }
                }}
                onClick={() => setShowReadByDialog(true)}
              >
                <CheckCircleIcon fontSize="small" sx={{ color: '#0C826A' }} />
                <Typography variant="caption" sx={{
                  fontFamily: 'FuturaPT-Book, Open Sans, sans-serif',
                  color: '#0C826A'
                }}>
                  Read by {email.readCount} Building Passport user{email.readCount > 1 ? 's' : ''}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      <Box sx={{ flexGrow: 1, overflow: isMobile ? 'visible' : 'auto' }}>

        {/* Email Body */}
        <Box sx={{ p: { xs: 1.5, md: 2 }, borderBottom: '1px solid #f2f2f2' }}>
          <Typography variant="subtitle1" sx={{
            fontWeight: 600,
            fontFamily: 'FuturaPT-Book, Open Sans, sans-serif',
            color: '#0C1229',
            mb: 1.5
          }}>
            Message
          </Typography>

          <Box
            sx={{
              bgcolor: 'white',
              border: isMobile ? 'none' : '1px solid #e6e6e6',
              borderRadius: 1,
              p: isMobile ? 0 : 2,
              minHeight: isMobile ? 'auto' : '150px',
              maxHeight: isMobile ? 'none' : '300px',
              overflow: isMobile ? 'visible' : 'auto',
              '&::-webkit-scrollbar': {
                width: '6px'
              },
              '&::-webkit-scrollbar-track': {
                background: '#f2f2f2'
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#cccccc',
                borderRadius: '4px'
              }
            }}
          >
            {email.bodyHtml ? (
              <div
                dangerouslySetInnerHTML={{ __html: cleanHtmlBody(email.bodyHtml) }}
                style={{
                  lineHeight: 1.7,
                  fontSize: '15px',
                  color: '#0C1229',
                  fontFamily: 'FuturaPT-Book, Open Sans, sans-serif',
                  wordBreak: 'break-word'
                }}
              />
            ) : email.bodyText ? (
              <Typography
                variant="body1"
                sx={{
                  whiteSpace: 'pre-wrap',
                  lineHeight: 1.7,
                  fontSize: '15px',
                  color: '#0C1229',
                  fontFamily: 'FuturaPT-Book, Open Sans, sans-serif',
                  wordBreak: 'break-word'
                }}
              >
                {email.bodyText}
              </Typography>
            ) : (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <EmailIcon sx={{ fontSize: 64, color: '#D0D5DD', mb: 2 }} />
                <Typography variant="h6" sx={{
                  color: '#84889C',
                  fontFamily: 'FuturaPT-Book, Open Sans, sans-serif',
                  mb: 1
                }}>
                  No Message Content
                </Typography>
                <Typography variant="body2" sx={{
                  color: '#84889C',
                  fontFamily: 'FuturaPT-Book, Open Sans, sans-serif'
                }}>
                  This email may contain only images or attachments
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {((email.processedFiles && email.processedFiles.length > 0) || (email.failedAttachmentsInfo && email.failedAttachmentsInfo.length > 0)) && (
          <Box sx={{ p: { xs: 1, md: 3 } }}>
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 3
            }}>
              <Typography variant="h6" sx={{
                fontWeight: 600,
                fontFamily: 'FuturaPT-Book, Open Sans, sans-serif',
                color: '#0C1229',
                fontSize: { xs: '1.1rem', md: '1.25rem' }
              }}>
                Attachments ({(email.processedFiles?.length || 0) + (email.failedAttachmentsInfo?.length || 0)})
              </Typography>

              {(hasFailedAttachments || hasProcessingAttachments) && (
                <Button
                  size="small"
                  startIcon={isRetryingAll ? <CircularProgress size={16} /> : <RefreshIcon />}
                  onClick={handleRetryAllAttachments}
                  disabled={isRetryingAll}
                  variant="outlined"
                  sx={{
                    borderRadius: 1,
                    fontFamily: 'FuturaPT-Book, Open Sans, sans-serif',
                    borderColor: '#D24724',
                    color: '#D24724',
                    textTransform: 'none',
                    '&:hover': {
                      borderColor: '#b13e1f',
                      backgroundColor: '#fcf4f2'
                    }
                  }}
                >
                  Retry All Failed
                </Button>
              )}
            </Box>

            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fill, minmax(220px, 1fr))' },
              gap: 1.5
            }}>
              {email.processedFiles?.map((file) => (
                <File
                  key={`file-${file.id}`}
                  file={file}
                  type={getFileTypeName(
                    pathOr('', ['file_type', 'id'], file) || pathOr('', ['fileType', 'id'], file),
                    fileTypes,
                    file.isPrivate
                  )}
                  viewMode="grid"
                  showThumbnail={true}
                  isInSearchBar={true} // Hides tags and versions
                  isShared={true} // Limits some edit functionality
                  hideBulkSelect={true} // Hide bulk edit checkbox
                  callback={handleFileUpdate}
                  categoryColor="#E6F7F4" // Light green for email files
                />
              ))}

              {/* Failed Attachments - Also in grid format */}
              {email.failedAttachmentsInfo?.map((attachment) => (
                <Box
                  key={`failed-${attachment.id}`}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    border: '1px solid #FAE4DE',
                    borderRadius: 1,
                    backgroundColor: 'white',
                    overflow: 'hidden',
                    minHeight: '160px',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      borderColor: '#D24724'
                    }
                  }}
                >
                  {/* Failed File Header */}
                  <Box sx={{
                    height: 80,
                    bgcolor: '#FCF4F2',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderBottom: '1px solid #FAE4DE'
                  }}>
                    <ErrorIcon sx={{ fontSize: 32, color: '#D24724', opacity: 0.7 }} />
                  </Box>

                  {/* Failed File Content */}
                  <Box sx={{ p: 1.5, flexGrow: 1 }}>
                    <Typography variant="subtitle2" sx={{
                      fontWeight: 600,
                      fontFamily: 'FuturaPT-Book, Open Sans, sans-serif',
                      color: '#0C1229',
                      mb: 1,
                      wordBreak: 'break-word'
                    }}>
                      {attachment.filename}
                    </Typography>

                    <Typography variant="caption" sx={{
                      color: '#D24724',
                      fontFamily: 'FuturaPT-Book, Open Sans, sans-serif',
                      fontWeight: 500,
                      display: 'block',
                      mb: 1
                    }}>
                      ✗ Upload failed
                      {attachment.size && ` • ${attachment.size}`}
                    </Typography>

                    <Typography variant="caption" sx={{
                      color: '#84889C',
                      fontFamily: 'FuturaPT-Book, Open Sans, sans-serif',
                      fontStyle: 'italic',
                      display: 'block',
                      mb: 2
                    }}>
                      {attachment.error || 'Unknown error occurred'}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>

            {/* Show message if no attachments at all */}
            {(!email.processedFiles?.length && !email.failedAttachmentsInfo?.length) && (
              <Box sx={{
                textAlign: 'center',
                py: 6,
                border: '1px dashed #D0D5DD',
                borderRadius: 1,
                bgcolor: '#fafbfc'
              }}>
                <AttachFileIcon sx={{ fontSize: 48, mb: 2, color: '#D0D5DD' }} />
                <Typography variant="h6" sx={{
                  color: '#84889C',
                  fontFamily: 'FuturaPT-Book, Open Sans, sans-serif',
                  mb: 1
                }}>
                  No Attachments
                </Typography>
                <Typography variant="body2" sx={{
                  color: '#84889C',
                  fontFamily: 'FuturaPT-Book, Open Sans, sans-serif'
                }}>
                  This email doesn't contain any file attachments
                </Typography>
              </Box>
            )}
          </Box>
        )}

      </Box>

      {/* Read By Dialog */}
      <Dialog open={showReadByDialog} onClose={() => setShowReadByDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontFamily: 'FuturaPT-Book, Open Sans, sans-serif', fontWeight: 600 }}>
          Read by Building Passport Users
        </DialogTitle>
        <DialogContent>
          {(() => {
            const readUsers = email.readByUsers ||
              (email.reads && email.reads.map(read => read.user).filter(Boolean)) ||
              [];

            if (readUsers.length > 0) {
              return (
                <List>
                  {readUsers.map((user, index) => (
                    <ListItem key={index} sx={{ px: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            backgroundColor: '#D24724',
                            fontSize: '0.8rem',
                            fontFamily: 'FuturaPT-Book, Open Sans, sans-serif'
                          }}
                        >
                          {user.name ? user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : '??'}
                        </Avatar>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontFamily: 'FuturaPT-Book, Open Sans, sans-serif', fontWeight: 600 }}>
                            {user.name || 'Unknown User'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'FuturaPT-Book, Open Sans, sans-serif' }}>
                            {user.email || 'No email'}
                          </Typography>
                        </Box>
                      </Box>
                    </ListItem>
                  ))}
                </List>
              );
            } else if (email.readCount > 0) {
              return (
                <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'FuturaPT-Book, Open Sans, sans-serif', py: 2, textAlign: 'center' }}>
                  This email has been read by {email.readCount} user{email.readCount > 1 ? 's' : ''}, but detailed information is not available.
                  <br />
                  <Typography variant="caption" sx={{ fontStyle: 'italic', mt: 1, display: 'block' }}>
                    Refresh the page to load user details.
                  </Typography>
                </Typography>
              );
            } else {
              return (
                <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'FuturaPT-Book, Open Sans, sans-serif', py: 2, textAlign: 'center' }}>
                  This email has not been read by any users yet.
                </Typography>
              );
            }
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowReadByDialog(false)} sx={{ fontFamily: 'FuturaPT-Book, Open Sans, sans-serif', textTransform: 'none' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmailDetail;
